import { useOAuthStore } from '@/features/auth/store';
import { useBrowserWalletStore } from '@/features/browserWallet/store';
// the store module only: the feature barrel also carries the flow's dialogs,
// and the sign-out one imports the logout routine.
import { useFlowStore } from '@/features/flow/store';
// the launcher store only, not the onboarding barrel: the barrel imports this
// module back through the flow's wallet-switch routine.
import { useOnboardingLauncher } from '@/features/onboarding/store';
import { purgeLegacyTradeSnapshot } from '@/features/trading/services/tradeSnapshot';
import { useWalletStore } from '@/features/wallet/store';
import { logoutOAuth } from '@/lib/api/auth';
import { logoutTradingSession } from '@/lib/api/tradingSession';
import { queryClient } from '@/lib/query';

import { broadcastTradingSession } from './broadcast';
import { useTradingSessionStore } from './store';

const LAST_OAUTH_PROVIDER_KEY = 'inout.oauth.last_provider';

/** Clears account-derived browser state after durable logout or HTTP 401. */
export function clearLocalAuthentication(): void {
    useWalletStore.getState().clearSession();
    useOAuthStore.getState().setMe({ authenticated: false });
    // NOT a sign out: a 401 or another tab's logout ends the social session,
    // but a held browser wallet key is its own identity and keeps V2 (plan
    // §4.3). Only an explicit Sign out wipes the key, below.
    useFlowStore.getState().onSessionLost();
    useTradingSessionStore.getState().reset();
    // a pending "give this social login an account" intent names the login that
    // just went away. The launcher keeps it sticky across relaunches on purpose,
    // so a sign-out is the only thing that can end it: left behind, the next
    // account's provisioning would bind a signed-out login's account hash to the
    // new owner's wallet.
    useOnboardingLauncher.getState().setAssociateSocial(null);
    purgeLegacyTradeSnapshot();
    queryClient.clear();
    try {
        sessionStorage.removeItem(LAST_OAUTH_PROVIDER_KEY);
    } catch {
        // Storage can be unavailable in hardened/private contexts.
    }
}

/**
 * Clears OAuth-derived signing authority after that identity is unlinked.
 * A passkey or owner-wallet session is independent and remains usable.
 */
export function clearLocalOAuthTradingAuthority(
    currentMethod = useTradingSessionStore.getState().status?.method,
): void {
    if (currentMethod === 'passkey' || currentMethod === 'eip712') return;
    useWalletStore.getState().clearSession();
    useTradingSessionStore.getState().reset();
    purgeLegacyTradeSnapshot();
    queryClient.clear();
}

interface AccountLogoutDependencies {
    revoke: () => Promise<void>;
    clearLocal: () => void;
    broadcast: () => void;
    /** Ends the flow choice and wipes the browser wallet key. */
    endFlows?: () => void;
}

/**
 * Sign out is the one place the browser wallet key is wiped (WolfSwap web
 * semantics): its backup stays on the server, restore it next time. The flow
 * goes back to unresolved and the tab's V1/V2 pick is dropped.
 */
function endFlowsOnSignOut(): void {
    useFlowStore.getState().reset();
    useBrowserWalletStore.getState().forget();
}

const defaultDependencies: AccountLogoutDependencies = {
    // BOTH server-side sessions. the social session is a separate cookie from the trading session:
    // revoking only the latter left /v2/auth/me answering `authenticated`, so the account came
    // straight back on the next reload and "Sign out" did nothing durable.
    revoke: async () => {
        await logoutTradingSession();
        await logoutOAuth();
    },
    clearLocal: clearLocalAuthentication,
    broadcast: () => broadcastTradingSession({ type: 'logout' }),
    endFlows: endFlowsOnSignOut,
};

/** Local state is retained if durable account-wide revocation fails. */
export async function performAccountLogout(
    dependencies: AccountLogoutDependencies = defaultDependencies,
): Promise<void> {
    await dependencies.revoke();
    dependencies.clearLocal();
    (dependencies.endFlows ?? endFlowsOnSignOut)();
    dependencies.broadcast();
}
